import { useEffect, useState } from "react";
import Header from "./Header";
import EventHeader from "./EventHeader";
import EventBody, { EventBodyDetailsList, EventBodyImage, EventBodyNoteList, EventBodyScheduleList } from "./EventBody";
import "../../styles/EventTemp.scss";
import CampHeroMedia from "./CampHeroMedia";
import EventRegistrationForm from "./EventRegistrationForm";
import { EVENT_REGISTRATION_EVENT_SCHEDULE } from "./eventRegistrationConfig";

/** Document order for scroll-spy (must match `#id` elements on this page — same order as contents.md). */
const CAMP_NAV_SCROLL_IDS = ["overview", "agenda", "audience", "schedule", "instructor", "apply"];

const CAMP_HEADER_NAV = [
  { label: "概要", href: "#overview", id: "overview" },
  { label: "アジェンダ", href: "#agenda", id: "agenda" },
  { label: "対象者", href: "#audience", id: "audience" },
  { label: "開催情報", href: "#schedule", id: "schedule" },
  { label: "講師", href: "#instructor", id: "instructor" },
  // { label: "参加登録", href: "#apply", id: "apply" },
];

export default function EventTemp() {
  const [activeNavId, setActiveNavId] = useState(null);

  useEffect(() => {
    const computeActive = () => {
      const sticky = document.querySelector(".event-temp-header-sticky");
      const offset = sticky?.getBoundingClientRect().height ?? 96;
      const probe = window.scrollY + offset + 8;
      let next = null;
      for (const id of CAMP_NAV_SCROLL_IDS) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top + window.scrollY;
        if (top <= probe) next = id;
      }
      setActiveNavId((prev) => (prev === next ? prev : next));
    };

    computeActive();
    window.addEventListener("scroll", computeActive, { passive: true });
    window.addEventListener("resize", computeActive);
    return () => {
      window.removeEventListener("scroll", computeActive);
      window.removeEventListener("resize", computeActive);
    };
  }, []);

  return (
    <div className="event-temp-page">
      <div className="event-temp-header-sticky">
        <Header activeNavId={activeNavId} navItems={CAMP_HEADER_NAV} />
      </div>
      <main className="event-temp-page__main">
        <CampHeroMedia
          imageName="uud_2"
          imageAlt="デザインプロセス解説ビジュアル"
          imageBgColor="#fff"
        />
        <EventHeader
          title="UXプロセスを実務に合わせてカスタマイズする方法 — 本質から理解する実践UX講座"
          subtitle="形だけのUXで終わらせず、プロジェクトの状況に合わせて実務で活用できるUXプロセス設計の考え方を学びます。"
          metaBadges={[
            { variant: "solid-cyan", text: "ONLINE" },
            { variant: "solid-green", text: "14:00–16:00" },
            { variant: "outline-cyan", text: "FREE" },
          ]}
          topicBadges={[
            // "24名/先着",
            "初心者向け",
            "中級者向け",
            "UXデザイン",
            "UXプロセス",
            "深い理解",
            "カスタマイズ",
            "実務向け",
            "複雑をほどく",
            "プロの考え方",
            "オンライン",
            "ライブQ&A"
          ]}
          imageName="uud_2"
          imageAlt="デザインプロセス解説ビジュアル"
          imageBgColor="#15a6f8"
          weekdayShort="SAT"
          dayNumber="30"
          monthShort="MAY"
          year="2026"
        />

        <EventBody
          id="overview"
          titlePrimary="概要"
          titleSecondary="Overview"
          body={
            <>
              <p>
                UXデザインプロセスは、プロダクトづくりにおいて、ユーザー理解から課題発見、アイデア検討、検証、改善までをつなぐ重要な考え方です。
              </p>
              <p>
                しかし、UXプロセスは一つの決まった型をそのまま使えばよいものではありません。
                プロジェクトの目的、規模、スケジュール、チーム体制によって、進め方を柔軟に変えていく必要があります。
              </p>
              <p>
                その一方で、「どこを調整してよいのか」「何を省略してはいけないのか」「そもそも各ステップの意味は何なのか」が曖昧なまま進められ、UXデザインプロセスに混乱や誤解が生まれているケースも少なくありません。
              </p>
              <p>
                IT業界で働いている方、デジタルプロダクトを開発している方、またはUX/UIデザインの世界に踏み出したばかりの方の中にも、
              </p>
              <p>
             UXデザインプロセスって、説明が曖昧すぎる…」
                「複雑そうで、結局どう実践すればいいのか分からない…」
                「プロジェクトごとに何を変えるべきなのか判断できない…」
                <br />
                そんなふうに感じたことがある方も多いのではないでしょうか。
              </p>
              <p>
                このセッションでは、UXデザインプロセスをできる限りシンプルかつ実践的に整理しながら、特に「状況に合わせて正しくカスタマイズする方法」を中心テーマとして解説していきます。
              </p>
              <p>
                プロジェクトの種類、開発スピード、チーム構成、予算、目的に合わせて、UXプロセスを柔軟に調整しながらも、本来の価値や効果を失わない実践的な考え方を学びます。
              </p>
              <p>
                また、多くのデザイナーやチームが陥りがちな誤解や、形だけになってしまったUXプロセスについても取り上げ、「どこを簡略化してよいのか」「逆に省略してはいけない部分は何か」を明確にしていきます。
              </p>
              <p>
                このセッションを終える頃には、UXデザインプロセスを“ただ知っている”状態ではなく、状況に合わせて正しく調整・説明・実践できる、よりプロフェッショナルな視点と自信を身につけられるはずです。
              </p>
              <p>
                「なんとなく理解しているUX」から、
                <br />
                「自信を持って実践・カスタマイズできるUX」へ。
              </p>
              <p>そんなステップアップを目指す方におすすめのセッションです。</p>
            </>
          }
        />

        <CampHeroMedia
          imageName="image_01"
          imageAlt="デザインプロセス解説ビジュアル"
          imageBgColor="#fff"
        />

        <EventBody
          id="agenda"
          titlePrimary="アジェンダ"
          titleSecondary="Schedule"
          body={
            <EventBodyScheduleList
              items={[
                { time: "14:00", title: "オープニング・セッション概要" },
                {
                  time: "14:05",
                  title: "UXデザインプロセスの基本理解",
                  description: "UXプロセスの目的・各ステップの役割と考え方",
                },
                {
                  time: "14:20",
                  title: "よくある誤解と失敗例",
                  description: "形だけのUXプロセス・間違った簡略化・現場で起こりやすい混乱",
                },
                { time: "14:40", title: "休憩" },
                {
                  time: "14:50",
                  title: "UXデザインプロセスを正しくカスタマイズする方法",
                  description:
                    "プロジェクト規模に合わせた調整・スケジュールとリソースの考え方・省略してはいけないポイント・実践的な調整例",
                },
                { time: "15:30", title: "ケーススタディ・実践的な考え方" },
                { time: "15:", title: "まとめ・Q&A" },
                { time: "16:00", title: "終了" },
              ]}
            />
          }
        />

        <EventBody
          id="audience"
          titlePrimary="対象者"
          titleSecondary="Audience"
          body={
            <EventBodyDetailsList
              items={[
                "UX/UIデザインを学び始めた方",
                "UXデザインプロセスを実践でどう使うべきか悩んでいる方",
                "UXプロセスを「なんとなく」で進めてしまっている方",
                "プロジェクトごとのUXプロセスの調整方法を学びたい方",
                "限られたスケジュールやリソースの中でUXを実践している方",
                "UXプロセスの省略・簡略化の判断に不安がある方",
                "UX/UIデザイナー、Webデザイナー、プロダクトデザイナー",
                "ディレクター、PM、開発チームなど、プロダクト開発に関わる方",
                "チームとしてUXデザインをより正しく実践したい方",
                "UXデザインを「説明できる・実践できる」レベルまで理解したい方",
              ]}
            />
          }
        />

        <EventBody
          id="schedule"
          titlePrimary="開催情報・ご案内"
          titleSecondary="Details"
          body={
            <>
              <div className="event-temp-info-facts">
                <p>
                  <span className="event-temp-event-body__inline-heading">日時</span><br />
                  {EVENT_REGISTRATION_EVENT_SCHEDULE}
                </p>
                <p>
                  <span className="event-temp-event-body__inline-heading">開催形式</span><br />
                  オンライン開催。<br />参加用URLは、お申し込みいただいた方へ開催前にメールでお送りします。
                </p>
                <p>
                  <span className="event-temp-event-body__inline-heading">イベント資料</span><br />
                  後日、登録メールアドレス宛に共有予定です。
                </p>
                <p>
                  <span className="event-temp-event-body__inline-heading">お申し込みについて</span><br />
                  <ul className="event-temp-callout__list">
                  <li>参加枠に限りがあるため、定員に達し次第受付を終了します。</li>
                  <li>
                    参加をご希望の方は、
                    <a href="#apply" className="event-temp-event-body__link">
                      参加登録フォーム
                    </a>
                    からお申し込みください。
                  </li>
                </ul>                </p>
                <p>
                  <span className="event-temp-event-body__inline-heading">ご参加にあたって</span><br />
                  <ul className="event-temp-callout__list">
                  <li>セッション内容は、当日の進行状況により一部変更になる場合があります。</li>
                  <li>ご質問へのご回答は、アジェンダ内のQ&Aの時間に限ります。</li>
                </ul>                </p>
              </div>

            </>
          }
        />

        <EventBody
          id="instructor"
          titlePrimary="講師"
          titleSecondary="Instructor"
          body={
            <>
              <p>
                日本でデザインを学んだ後、2006年よりウェブデザイナー／クリエイティブディレクターとして活動を開始し、2010年からUX/UIデザインに従事。
              </p>
              <p>
                これまで、国内外のスタートアップから大手企業まで、国際プロジェクトを含む多数のデジタルプロダクト開発に携わり、人間中心設計とデザイン思考をベースに、ビジネスとユーザーをつなぐUX/UIデザインを実践してきました。
              </p>
              <p>
                現在は、ZILMOO株式会社『JUUSANDO!』にてUX/UIデザインサービスを提供。
                また、東洋美術学校 や 日本電子専門学校 で講師を務めた経験を持ち、2013年にはデザインコミュニティ{" "}
                <a href="https://uud.io/" target="_blank" rel="noopener noreferrer" className="event-temp-event-body__link">
                  uud.io
                </a>{" "}
                を設立。
              </p>
              <p>
                現在は、法人向けUX/UI研修やコミュニティ活動を通じて、実践的なUX/UIデザインの普及と、次世代デザイナーの育成に取り組んでいます。
              </p>
            </>
          }
        >
          <EventBodyImage imageName="hafid" imageAlt="講師の肖像" caption="はふいど先生" />
        </EventBody>

        <section id="apply" className="event-reg-section" aria-label="参加登録">
          <div className="event-reg-section__inner">
            <EventRegistrationForm />
          </div>
        </section>
      </main>
    </div>
  );
}
